import { EUserType, TWebinarInviteesType } from '@src/types';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { toast } from 'react-toastify';
import { useSendWebinarInvitation } from '@src/hooks';
import { SuccessToast } from '@src/components';
import { IAttendee } from '@src/webinar/types';

const useInviteModal = ({
  userRole,
  email,
  onClose,
  attendee,
  setAttendeesIdList,
}: {
  userRole?: TWebinarInviteesType;
  email: string;
  onClose: () => void;
  attendee?: IAttendee;
  setAttendeesIdList: (attendees: (prev: Set<string>) => Set<string>) => void;
}) => {
  const [role, setRole] = useState(EUserType.SPEAKER);
  const [emails, setEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(``);
  const { id = '' } = useParams();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { mutate: sendWebinarInvitation, isPending: isLoading } = useSendWebinarInvitation({
    isReinvite: !!userRole,
    onSuccess: (response) => {
      resetForm();
      onClose();
      toast(<SuccessToast message={`${response.message}`} />, {
        className: 'custom-toast-wrapper',
        autoClose: 3000,
        closeButton: false,
        hideProgressBar: true,
        icon: false,
      });
    },
    onError: (error) => {
      if (attendee?._id && error.includes('already joined')) {
        setAttendeesIdList?.((prev) => {
          const newSet = new Set(prev);
          newSet.add(attendee._id);
          return newSet;
        });
        resetForm();
        onClose();
      }
    },
  });

  const [editorMaxHeight, setEditorMaxHeight] = useState(140);

  useLayoutEffect(() => {
    if (userRole && email) {
      setRole(userRole);
      setEmails([email]);
    }
  }, [userRole, email]);

  useEffect(() => {
    const updateHeight = () => {
      const height = window.innerHeight;
      const width = window.innerWidth;

      const isMobile = width <= 768;

      if (isMobile) {
        setEditorMaxHeight(height * 0.24);
      } else if (height < 800) {
        setEditorMaxHeight(120);
      } else if (height < 1000) {
        setEditorMaxHeight(140);
      } else {
        setEditorMaxHeight(250);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const handleAddEmail = async () => {
    if (!emails.length) {
      toast.error('Please enter at least one email.', { icon: false });
      return;
    }

    await sendWebinarInvitation({
      roomId: id!.toString(),
      userType: role as TWebinarInviteesType,
      recipients: emails,
      emails,
      emailContent: {
        subject,
        body,
        footer: `Best regards`,
      },
    });
  };

  const resetForm = () => {
    setEmails([]);
    setSubject('');
    setBody('');
    setRole(EUserType.SPEAKER);
  };

  return {
    role,
    emails,
    subject,
    body,
    isLoading,
    handleAddEmail,
    resetForm,
    setRole,
    setEmails,
    setSubject,
    setBody,
    setEditorMaxHeight,
    editorMaxHeight,
    isMobile,
  };
};

export default useInviteModal;
